import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';

// third-party
import { Chance } from 'chance';

// reducer - state management
import { LOGOUT, SETQRIMAGE, REQUIRESETPASSWORD } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import axios from 'utils/axios';

import {
    userLoginRequest,
    verifyLoginOtp,
    verifySession,
    logout as logoutSession,
    resendOtp as resendLoginOtp,
    changePassword,
    userValidateInitLogin,
    setPassword,
    reInitDeployment
} from 'api/auth';
import { IdleTimerProvider } from 'react-idle-timer';
import { getSettings } from 'api/settings';
import { useDispatch } from 'store';
import { setClientSettings } from 'store/slices/settings';

const chance = new Chance();

// constant
const initialState = {
    isLoggedIn: false,
    isInitialized: false,
    user: null,
    isSessionTimeout: false,
    otpCode: false,
    qrImage: null,
    isKeepMeLogin: false,
    initLogin: false,
    requireSetPassword: false,
    groupId: null,
    deployments: []
};

// const setSession = (serviceToken, loginType = LOGIN_TYPE['learner']) => {
//     if (serviceToken) {
//         localStorage.setItem('serviceToken', serviceToken);
//         axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;

//         // for login type
//         localStorage.setItem('loginType', loginType);
//     } else {
//         localStorage.removeItem('serviceToken');
//         delete axios.defaults.headers.common.Authorization;

//         localStorage.removeItem('loginType');
//     }
// };

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
export const setOTPSessionKey = (sessionKey) => {
    if (sessionKey) {
        localStorage.setItem('otpSessionKey', sessionKey);
    }
};

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);

    /**
     * Handle Session Timeout detection
     * To show re enter password modal
     */
    const handleSessionTimeout = (exp) => {
        const now = new Date();
        const timeoutMilliSeconds = exp * 1000 - now.getTime();

        if (timeoutMilliSeconds > 0) {
            // trigger the timeout
            setTimeout(() => {
                dispatch({
                    type: 'SESSION_TIMEOUT',
                    payload: {
                        isSessionTimeout: true
                    }
                });
            }, timeoutMilliSeconds);
        }
    };

    const dispatchStore = useDispatch();

    const initialize = async () => {
        try {
            const response = await verifySession();
            if (response?.success) {
                const { user, deployments = [] } = response;
                dispatch({
                    type: 'LOGIN',
                    payload: {
                        isLoggedIn: true,
                        user,
                        deployments
                    }
                });
                handleSessionTimeout(user?.exp);
                dispatch({
                    type: REQUIRESETPASSWORD,
                    payload: {
                        requireSetPassword: response?.requireSetPassword || false
                    }
                });
            } else {
                dispatch({ type: LOGOUT });
            }
        } catch (err) {
            console.log('err', err);
            dispatch({ type: LOGOUT });
        }

        try {
            const response = await getSettings();
            dispatchStore(setClientSettings(response));
        } catch (err) {
            console.log('err getSettings', err);
        }
    };

    useEffect(() => {
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatchStore]);

    /**
     * isRefresh - used for session timeout continue using application.
     */
    const login = async ({ email, password, isRefresh = false, hasCaptcha = false, isKeepMeLogin = false, groupId = null }) => {
        const response = await userLoginRequest({ email, password, isRefresh, hasCaptcha, groupId });
        if (!response) {
            throw new Error('Login failed');
        }

        if (!response?.success) throw new Error(response.message || 'Login failed');

        // if is Refresh we will recall the handle session timeout
        if (isRefresh) {
            dispatch({
                type: 'SESSION_TIMEOUT',
                payload: {
                    isSessionTimeout: false
                }
            });
            handleSessionTimeout(response?.user?.exp);
        } else {
            dispatch({
                type: 'LOGIN_REQUEST_SUCCESS',
                payload: {
                    user: response.user,
                    otpCode: response.user?.otpCode || false,
                    qrImage: response.user?.qrImage || null,
                    isKeepMeLogin,
                    groupId
                }
            });
        }
        return response;
    };

    const register = async (email, password, firstName, lastName) => {
        // todo: this flow need to be recode as it not verified
        const id = chance.bb_pin();
        const response = await axios.post('/api/account/register', {
            id,
            email,
            password,
            firstName,
            lastName
        });
        let users = response.data;

        if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
            const localUsers = window.localStorage.getItem('users');
            users = [
                ...JSON.parse(localUsers),
                {
                    id,
                    email,
                    password,
                    name: `${firstName} ${lastName}`
                }
            ];
        }

        window.localStorage.setItem('users', JSON.stringify(users));
    };

    const logout = (email = null) => {
        if (email) {
            logoutSession({ email });
        }
        dispatch({ type: LOGOUT });

        // reset also initLogin to false if call logout
        dispatch({
            type: SETQRIMAGE,
            payload: { initLogin: false }
        });
    };

    const resetPassword = async (userId, password, groupId = '') => {
        await changePassword(userId, password, groupId);
        return true;
    };

    const updateProfile = () => {};

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    const verifyOtp = async ({ email, otp, method, hasCaptcha = false, initialLoginToken = '' }) => {
        const otpSessionKey = localStorage.getItem('otpSessionKey');
        const response = await verifyLoginOtp({
            email,
            otp,
            method,
            hasCaptcha,
            otpSessionKey,
            isKeepMeLogin: state?.isKeepMeLogin,
            groupId: state?.groupId,
            initialLoginToken
        });
        if (!response) {
            throw new Error('Verification failed');
        }

        await processSuccessLogin(response?.data?.deploymentGroupInfo?.activeDeployment || '');

        dispatch({
            type: REQUIRESETPASSWORD,
            payload: {
                requireSetPassword: response?.data?.requireSetPassword || false
            }
        });

        return response;
    };

    const resendOtp = async (id) => {
        const response = await resendLoginOtp(id);
        if (!response) {
            throw new Error('Resend OTP failed');
        }

        if (response.data) {
            // set the otpSessionKey
            const { otpSessionKey } = response.data;
            setOTPSessionKey(otpSessionKey);
        }
        return response;
    };

    // Inactivity auto logout
    const handleOnIdle = async () => {
        // do only if pathname is not login
        if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
            const { user } = state;
            logout(user?.email);
        }
    };

    // validate initial login token and email
    const validateInitLogin = async (email, token) => {
        const response = await userValidateInitLogin(email, token);
        const { id, tempSecret, otpSessionKey } = response.data;
        setOTPSessionKey(otpSessionKey);
        dispatch({
            type: SETQRIMAGE,
            payload: { id, tempSecret, initLogin: true }
        });

        // dispatch login request for resend OTP
        dispatch({
            type: 'LOGIN_REQUEST_SUCCESS',
            payload: {
                user: { _id: id }
            }
        });
    };

    const setUserPassword = async (password) => {
        try {
            const data = await setPassword(password);
            dispatch({
                type: REQUIRESETPASSWORD,
                payload: {
                    requireSetPassword: false
                }
            });
            return data;
        } catch (e) {
            throw new Error(e.message);
        }
    };

    // reinit deployment based on global select
    const processReInitDeployment = async (deployment) => {
        try {
            const data = await reInitDeployment(deployment);
            await processSuccessLogin(deployment);
            return data;
        } catch (e) {
            throw new Error(e.message);
        }
    };

    const processSuccessLogin = async (deployment) => {
        // we will save to state the current deployment
        localStorage.setItem('active-deployment', deployment);

        /**
         * Call again the verify session for user information
         */
        const sessionResponse = await verifySession();
        const { user, deployments = [] } = sessionResponse;

        dispatch({
            type: 'LOGIN',
            payload: {
                user,
                deployments
            }
        });

        handleSessionTimeout(user?.exp);
    };

    return (
        <IdleTimerProvider
            timeout={1000 * 60 * (process.env.REACT_APP_INACTIVITY_TIMEOUT || 45)}
            onIdle={handleOnIdle}
            crossTab={true}
            name="MY_APP_IDLE_TIMER"
        >
            <JWTContext.Provider
                // eslint-disable-next-line prettier/prettier
                value={{
                    ...state,
                    login,
                    logout,
                    register,
                    resetPassword,
                    updateProfile,
                    verifyOtp,
                    refetchSession: initialize,
                    resendOtp,
                    validateInitLogin,
                    setUserPassword,
                    processReInitDeployment
                }}
            >
                {children}
            </JWTContext.Provider>
        </IdleTimerProvider>
    );
};

JWTProvider.propTypes = {
    children: PropTypes.node
};

export default JWTContext;
